import { isEqual, unionWith } from 'lodash';
import { toSlug } from './utils';

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

const comparator = (a: unknown, b: unknown) => {
  if (a && b && typeof a === 'object' && typeof b === 'object'
    && 'id' in a && 'id' in b && a.id && b.id) {
    return a.id === b.id;
  }

  return isEqual(a, b);
};

export const addItemsToDataLayer = (items: unknown[] | undefined, itemType: string) => {
  if (!window) {
    return;
  }

  let pastItems: unknown[] = [];
  let finalItems: unknown[] = [];
  const slugifiedType = toSlug(itemType) || '';

  // Get current items (store them on client window)
  // Only useful for array of items, otherwise just replace it
  const stored: unknown = Reflect.get(window, slugifiedType);
  if (Array.isArray(stored)) {
    const saved: unknown[] = stored;
    pastItems = saved;
  }

  // Merge new items with old items while avoiding duplicates
  const currentItems = items || [];
  finalItems = unionWith(pastItems, currentItems, comparator);

  // Only push to dataLayer if a new item has been added
  if (finalItems.length !== pastItems.length) {
    // Set new items to window
    const dataLayerObject: Record<string, unknown> = {};
    dataLayerObject[slugifiedType] = finalItems;
    dataLayerObject.event = `Add ${itemType} to dataLayer`;

    // Push to dataLayer and update window arrays
    Reflect.set(window, slugifiedType, finalItems);

    if (window.dataLayer) {
      window.dataLayer.push(dataLayerObject);
    }
  }
};

export const addObjToDataLayer = (obj: unknown, objKey: string) => {
  if (window) {
    const dataLayerObject: Record<string, unknown> = {};
    dataLayerObject[objKey] = obj;
    dataLayerObject.event = `Add ${objKey} to dataLayer`;

    if (window.dataLayer) {
      window.dataLayer.push(dataLayerObject);
    }
  }
};

export const addEventToDataLayer = (eventName: string, eventProps: Record<string, unknown> = {}) => {
  if (window) {
    const dataLayerObject = { ...eventProps };
    dataLayerObject.event = eventName;

    if (window.dataLayer) {
      window.dataLayer.push(dataLayerObject);
    }
  }
};

export const dataLayerIdGenerator = (itemType: string, slug: string | null | undefined) => {
  const lowerItemType = toSlug(itemType);
  return `${lowerItemType}_${toSlug(slug)}`;
};

export const dataLayerIdGeneratorNewItem = <T extends object>(
  itemType: string, item: T, dataLayerId?: string | null,
) => {
  let newDataLayerId = dataLayerId;
  if (!dataLayerId) {
    const itemSlug = 'slug' in item && typeof item.slug === 'string' ? item.slug : '';
    newDataLayerId = dataLayerIdGenerator(itemType, itemSlug);
  }

  const newItem: T & { dataLayerId?: string | null } = { ...item };

  newItem.dataLayerId = newDataLayerId;
  return newItem;
};
